import { useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  Divider,
  Button,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import GavelIcon from '@mui/icons-material/Gavel';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';

interface Section {
  id: string;
  title: string;
  paragraphs: Array<{ text: string; isList?: boolean; items?: string[] }>;
}

const TERMS_SECTIONS: Section[] = [
  {
    id: 'preamble',
    title: '',
    paragraphs: [
      {
        text: 'Welcome to CPAx Academy ("Platform"), owned and operated by CPA Crossings, LLC ("CPA Crossings," "we," "us," or "our"), located at 121 E. 3rd Street, Rochester, Michigan 48307.',
      },
      {
        text: 'These Terms and Conditions ("Terms") govern your access to and use of the CPAx Academy website, learning platform, services, educational content, webinars, AI-powered tools, and related features (collectively, the "Services"). By accessing or using the Services, you agree to be bound by these Terms.',
      },
      {
        text: 'If you do not agree to these Terms, you may not access or use the Services.',
      },
    ],
  },
  {
    id: 'eligibility',
    title: '1. Eligibility and Account Registration',
    paragraphs: [
      { text: 'To access certain features of the Services, users may be required to create an account.' },
      { text: 'You agree to:' },
      {
        isList: true,
        text: '',
        items: [
          'Provide accurate and complete registration information;',
          'Maintain and promptly update your information as necessary;',
          'Maintain the confidentiality of your username and password; and',
          'Accept responsibility for all activities occurring under your account.',
        ],
      },
      {
        text: 'You must notify CPA Crossings immediately of any unauthorized use of your account or security breach.',
      },
      {
        text: 'CPA Crossings reserves the right to suspend, restrict, or terminate accounts at its sole discretion, including for violations of these Terms or misuse of the Services.',
      },
    ],
  },
  {
    id: 'educational-services',
    title: '2. Educational Services',
    paragraphs: [
      { text: 'CPAx Academy provides:' },
      {
        isList: true,
        text: '',
        items: [
          'On-demand educational courses;',
          'Live webinars and virtual learning events;',
          'Continuing Professional Education ("CPE") tracking;',
          'AI-powered educational recommendations and assistance; and',
          'Related learning management services.',
        ],
      },
      { text: 'The Services are intended solely for educational and professional development purposes.' },
    ],
  },
  {
    id: 'intellectual-property',
    title: '3. Intellectual Property Rights',
    paragraphs: [
      {
        text: 'All content available through the Services, including but not limited to course materials, webinar content, text, graphics, videos, trademarks, logos, software, platform design, AI-generated outputs, and other materials are owned by CPA Crossings or its licensors and are protected by intellectual property laws.',
      },
      {
        text: 'Course content may be licensed from third-party providers and remains protected by applicable copyright and licensing restrictions.',
      },
      {
        text: 'Except as expressly authorized in writing, users may not copy, reproduce, distribute, republish, modify, create derivative works from, publicly display, sell, sublicense, or otherwise exploit any content from the Services.',
      },
      {
        text: 'Users are not permitted to upload, publish, or distribute user-generated educational content through the Platform unless expressly authorized by CPA Crossings.',
      },
    ],
  },
  {
    id: 'limited-license',
    title: '4. Limited License',
    paragraphs: [
      {
        text: 'CPA Crossings grants users a limited, non-exclusive, non-transferable, revocable license to access and use the Services solely for personal educational and professional purposes.',
      },
      { text: 'This license does not transfer ownership rights of any kind.' },
    ],
  },
  {
    id: 'ai-disclaimer',
    title: '5. AI-Powered Features Disclaimer',
    paragraphs: [
      {
        text: 'The Platform may include AI-powered tools and chatbot functionality that provide educational assistance, recommendations, summaries, or course guidance.',
      },
      {
        text: 'These AI-generated responses are provided solely for informational and educational purposes and:',
      },
      {
        isList: true,
        text: '',
        items: [
          'do not constitute accounting, tax, legal, audit, financial, or professional advice;',
          'may contain inaccuracies or incomplete information; and',
          'should not be relied upon as a substitute for professional judgment.',
        ],
      },
      {
        text: 'Users are solely responsible for independently verifying information and exercising appropriate professional discretion.',
      },
      {
        text: 'CPA Crossings makes no representations or warranties regarding the accuracy, reliability, or completeness of AI-generated outputs.',
      },
    ],
  },
  {
    id: 'cpe',
    title: '6. Continuing Professional Education (CPE)',
    paragraphs: [
      { text: 'CPA Crossings is a NASBA-approved provider of Continuing Professional Education programs.' },
      {
        text: 'The Platform may provide tools for tracking course completion and earned CPE credits. Users are responsible for maintaining accurate records and ensuring compliance with any applicable licensing or reporting obligations.',
      },
      {
        text: 'CPE eligibility and issuance are subject to successful completion of applicable program requirements, including attendance, participation, and assessment criteria where applicable.',
      },
    ],
  },
  {
    id: 'prohibited-conduct',
    title: '7. Prohibited Conduct',
    paragraphs: [
      { text: 'Users agree not to:' },
      {
        isList: true,
        text: '',
        items: [
          'violate any applicable law or regulation;',
          'share account credentials with unauthorized individuals;',
          'attempt to gain unauthorized access to the Platform or related systems;',
          'interfere with platform security or operations;',
          'scrape, harvest, or extract platform data;',
          'reverse engineer or attempt to derive source code;',
          'copy or redistribute course materials;',
          'use the Services for unlawful, fraudulent, or abusive purposes; or',
          'misuse AI tools or attempt to generate harmful, infringing, or unlawful content.',
        ],
      },
      {
        text: 'CPA Crossings reserves the right to investigate and take appropriate action regarding violations.',
      },
    ],
  },
  {
    id: 'service-availability',
    title: '8. Service Availability',
    paragraphs: [
      {
        text: 'CPA Crossings does not guarantee uninterrupted or error-free operation of the Services.',
      },
      {
        text: 'The Platform may occasionally experience scheduled maintenance, technical difficulties, outages, security incidents, or service interruptions.',
      },
      {
        text: 'CPA Crossings reserves the right to modify, suspend, or discontinue any aspect of the Services at any time without liability.',
      },
    ],
  },
  {
    id: 'third-party',
    title: '9. Third-Party Services and Links',
    paragraphs: [
      {
        text: 'The Services may contain links to third-party websites, webinar platforms, content providers, or integrations.',
      },
      {
        text: 'CPA Crossings does not control and is not responsible for third-party content, privacy practices, or services.',
      },
      { text: 'Accessing third-party services is at the user\'s own risk.' },
    ],
  },
  {
    id: 'international',
    title: '10. International Users',
    paragraphs: [
      { text: 'The Services may be accessed by users outside the United States.' },
      {
        text: 'Users accessing the Platform from outside the United States are responsible for compliance with local laws and regulations applicable to their use of the Services.',
      },
    ],
  },
  {
    id: 'disclaimers',
    title: '11. Disclaimer of Warranties',
    paragraphs: [
      {
        text: 'THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE.',
      },
    ],
  },
  {
    id: 'limitation',
    title: '12. Limitation of Liability',
    paragraphs: [
      {
        text: 'TO THE MAXIMUM EXTENT PERMITTED BY LAW, CPA CROSSINGS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES.',
      },
    ],
  },
  {
    id: 'indemnification',
    title: '13. Indemnification',
    paragraphs: [
      {
        text: 'You agree to indemnify, defend, and hold harmless CPA Crossings and its affiliates from claims arising from your use of the Services or violation of these Terms.',
      },
    ],
  },
  {
    id: 'arbitration',
    title: '14. Arbitration Agreement and Class Action Waiver',
    paragraphs: [
      {
        text: 'Any dispute arising out of or relating to these Terms or the Services shall be resolved exclusively through binding arbitration administered in the State of Michigan.',
      },
      { text: 'Users waive the right to participate in class actions or collective proceedings.' },
    ],
  },
  {
    id: 'governing-law',
    title: '15. Governing Law',
    paragraphs: [
      {
        text: 'These Terms shall be governed by and construed in accordance with the laws of the State of Michigan.',
      },
    ],
  },
  {
    id: 'changes',
    title: '16. Changes to Terms',
    paragraphs: [
      { text: 'CPA Crossings reserves the right to modify these Terms at any time.' },
    ],
  },
  {
    id: 'contact',
    title: '17. Contact Information',
    paragraphs: [
      {
        text: 'CPA Crossings, LLC\n121 E. 3rd Street\nRochester, MI 48307\nEmail: hello@cpacrossings.com',
      },
    ],
  },
];

const PRIVACY_SECTIONS: Section[] = [
  {
    id: 'privacy-preamble',
    title: '',
    paragraphs: [
      {
        text: 'CPA Crossings, LLC ("CPA Crossings," "we," "our," or "us") respects your privacy and is committed to protecting the personal information you provide through CPAx Academy and related services.',
      },
    ],
  },
  {
    id: 'info-collected',
    title: '1. Information We Collect',
    paragraphs: [
      { text: 'We may collect:' },
      {
        isList: true,
        text: '',
        items: [
          'Name',
          'Email address',
          'Organization/employer information',
          'Account credentials',
          'Course enrollment and completion data',
          'Continuing Professional Education (CPE) tracking information',
          'Webinar participation records',
          'Communications with support or AI tools',
          'IP address and device/browser information',
          'Usage analytics and interaction data',
        ],
      },
    ],
  },
  {
    id: 'info-use',
    title: '2. How We Use Information',
    paragraphs: [
      { text: 'We use information to:' },
      {
        isList: true,
        text: '',
        items: [
          'Provide educational services and platform access;',
          'Deliver webinars and on-demand content;',
          'Track and report CPE completion;',
          'Improve platform functionality and learning experiences;',
          'Operate AI-powered educational tools;',
          'Respond to inquiries and support requests;',
          'Communicate updates and administrative notices;',
          'Maintain security and prevent fraud; and',
          'Comply with legal obligations.',
        ],
      },
    ],
  },
  {
    id: 'ai-data',
    title: '3. AI Tools and Data Usage',
    paragraphs: [
      {
        text: 'Interactions with AI-powered features may be processed to improve educational assistance, platform functionality, and user experience.',
      },
      {
        text: 'Users should not submit confidential, sensitive, or regulated information into AI chat or recommendation tools.',
      },
    ],
  },
  {
    id: 'cookies',
    title: '4. Cookies and Analytics',
    paragraphs: [
      {
        text: 'We may use cookies, analytics tools, and similar technologies to maintain sessions, analyze usage patterns, and improve performance.',
      },
    ],
  },
  {
    id: 'sharing',
    title: '5. Sharing of Information',
    paragraphs: [
      {
        text: 'We may share information with learning content licensors, webinar hosting providers, analytics providers, technology vendors, and legal authorities where required by law.',
      },
      { text: 'We do not sell personal information.' },
    ],
  },
  {
    id: 'security',
    title: '6. Data Security',
    paragraphs: [
      {
        text: 'CPA Crossings uses reasonable administrative, technical, and organizational safeguards to protect personal information.',
      },
    ],
  },
  {
    id: 'international-privacy',
    title: '7. International Users',
    paragraphs: [
      {
        text: 'Users accessing the Services outside the United States understand that information may be processed and stored in the United States.',
      },
    ],
  },
  {
    id: 'childrens-privacy',
    title: '8. Children\'s Privacy',
    paragraphs: [
      {
        text: 'The Services are intended primarily for educational and professional users.',
      },
    ],
  },
  {
    id: 'user-rights',
    title: '9. User Rights and Choices',
    paragraphs: [
      {
        text: 'Users may request access, correction, deletion, or information regarding personal data practices by contacting hello@cpacrossings.com.',
      },
    ],
  },
  {
    id: 'third-party-privacy',
    title: '10. Third-Party Services',
    paragraphs: [
      { text: 'The Services may link to or integrate with third-party platforms.' },
    ],
  },
  {
    id: 'policy-changes',
    title: '11. Changes to This Privacy Policy',
    paragraphs: [
      { text: 'CPA Crossings may update this Privacy Policy periodically.' },
    ],
  },
  {
    id: 'privacy-contact',
    title: '12. Contact Information',
    paragraphs: [
      {
        text: 'CPA Crossings, LLC\n121 E. 3rd Street\nRochester, MI 48307\nEmail: hello@cpacrossings.com',
      },
    ],
  },
];

const EFFECTIVE_DATE = '[Insert Date]';

function SectionList({ sections }: { sections: Section[] }) {
  return (
    <Stack spacing={0}>
      {sections.map((section, idx) => (
        <Box key={section.id} sx={{ mb: 4 }}>
          {section.title && (
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, fontSize: '18px', mb: 1.5, color: 'text.primary' }}
            >
              {section.title}
            </Typography>
          )}
          <Stack spacing={1.25}>
            {section.paragraphs.map((para, pIdx) =>
              para.isList && para.items ? (
                <List key={pIdx} dense disablePadding sx={{ pl: 2 }}>
                  {para.items.map((item, iIdx) => (
                    <ListItem key={iIdx} disableGutters sx={{ py: 0.25, alignItems: 'flex-start' }}>
                      <Typography variant="body2" sx={{ mr: 1, mt: '2px', color: 'text.secondary' }}>
                        •
                      </Typography>
                      <ListItemText
                        primary={item}
                        primaryTypographyProps={{
                          variant: 'body1',
                          sx: { fontSize: '15px', lineHeight: 1.65, color: 'text.secondary' },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography
                  key={pIdx}
                  variant="body1"
                  sx={{
                    fontSize: '15px',
                    lineHeight: 1.65,
                    color: 'text.secondary',
                    whiteSpace: 'pre-line',
                  }}
                >
                  {para.text}
                </Typography>
              )
            )}
          </Stack>
          {idx < sections.length - 1 && <Divider sx={{ mt: 3 }} />}
        </Box>
      ))}
    </Stack>
  );
}

export default function TermsOfService() {
  const [tab, setTab] = useState(0);

  return (
    <Stack spacing={3} sx={{ px: 3 }}>
      {/* Header */}
      <Box>
        <Typography
          variant="h4"
          sx={{
            fontSize: '32px',
            fontWeight: 400,
            lineHeight: 1.235,
            letterSpacing: '0.00735em',
            color: 'text.primary',
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {tab === 0 ? (
            <GavelIcon color="primary" sx={{ fontSize: 32 }} />
          ) : (
            <PrivacyTipIcon color="primary" sx={{ fontSize: 32 }} />
          )}
          {tab === 0 ? 'Terms and Conditions' : 'Privacy Policy'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Effective date: {EFFECTIVE_DATE}
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="legal documents">
          <Tab label="Terms and Conditions" />
          <Tab label="Privacy Policy" />
        </Tabs>
      </Box>

      {/* Main content */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}>
        <CardContent sx={{ p: 3 }}>
          <Stack spacing={0}>
            {/* Intro */}
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {tab === 0
                ? 'Please read these Terms and Conditions carefully before using the CPAx Academy platform operated by CPA Crossings, LLC. These Terms govern your access to and use of the Services.'
                : 'Please read this Privacy Policy carefully. It describes how CPA Crossings, LLC collects, uses, and shares information through CPAx Academy and related services.'}
            </Typography>

            <Divider sx={{ mb: 3 }} />

            {tab === 0 ? (
              <SectionList sections={TERMS_SECTIONS} />
            ) : (
              <SectionList sections={PRIVACY_SECTIONS} />
            )}

            {/* Footer actions */}
            <Divider sx={{ mb: 3 }} />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1.5,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Questions about these terms?
              </Typography>
              <Button component={RouterLink} to="/app/contact" variant="outlined" size="small">
                Contact Us
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
